// region Caching Popover2

import {classNames, observer, Popover2InteractionKind, React, useAsObservableSource, useEffect, useLocalStore, useMemo} from '@api'
import {Popover2, IPopover2Props} from '@api'
import * as css from './MyPopover2.css'
import {action, autorun, observable, reaction} from 'mobx'
import {site, ui} from '@stores'

type MyPopover2Props = { cache?: boolean, onOpened?: (node: HTMLElement) => void, onClosed?: (node: HTMLElement) => void } & Omit<IPopover2Props<any>, 'onOpening' | 'onClosing' | 'onOpened' | 'onClosed'>

export class MyPopover2Store {
  constructor(public props: Pick<MyPopover2Props, 'interactionKind' | 'onClosed' | 'onOpened' | 'onInteraction' | 'isOpen'>) {

  }

  registerEffects = () => {
    const toDispose = [
      reaction(() => this.open, (open) => {
        if (open) {
          if (site.currentOpenPopover && site.currentOpenPopover != this) {
            site.currentOpenPopover.open = false
            site.currentOpenPopover.props.onInteraction && site.currentOpenPopover.props.onInteraction(false)
          }
          site.currentOpenPopover = this
        }

        const {props: {onOpened, onClosed}, node} = this
        open && onOpened && onOpened(node)
        !open && onClosed && onClosed(node)
      }),

      autorun(() => {
        const {props: {isOpen}} = this
        if (_.isBoolean(isOpen)) {
          this.open = isOpen
        }
      })
    ]

    return () => toDispose.forEach(d => d())
  }

  @action onOpening = (node: HTMLElement) => {
    this.node = node
  }

  @observable.ref node: HTMLElement

  @observable open = false
  @observable private justClosedByBlueprint = false

  @action toggleOpen = () => {
    this.open = !this.open
  }

  @action onClickWrapper = (e: React.MouseEvent<HTMLDivElement>) => {
    const {props: {isOpen, interactionKind}, justClosedByBlueprint} = this

    if ((interactionKind === 'click' || !interactionKind) && !_.isBoolean(isOpen) && !justClosedByBlueprint && !(e.target as HTMLElement).closest('.bp3-popover2')) {
      this.toggleOpen()
    }
  }

  isMouseInTargetOrPopover = false
  setRef = r => this.ref = r
  @observable.ref ref : Popover2<HTMLDivElement>
  hoverTimeout: number
  clearHoverTimeout: number
  @action handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    this.isMouseInTargetOrPopover = true

    const {props: {interactionKind}} = this
    if (interactionKind === 'hover') {
      if (this.open) {
        clearTimeout(this.clearHoverTimeout)
      }
      else {
        this.hoverTimeout = window.setTimeout(() => {
          this.open = true
        }, ui.settings.HOVER_TIMEOUT)
      }
    }
  }

  @action handleMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
    this.isMouseInTargetOrPopover = false
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout)
    }

    if (this.open) {
      this.clearHoverTimeout = window.setTimeout(() => {
        this.open = false
      }, ui.settings.HOVER_TIMEOUT * 2)
    }
  }


  @action onInteraction = (nextOpenState: boolean, e?: React.SyntheticEvent<HTMLElement>) => {
    const {props: {onInteraction, isOpen}} = this

    if (!_.isBoolean(isOpen)) {
      this.justClosedByBlueprint = this.open && !nextOpenState
      if (!nextOpenState && this.open) {
        this.open = nextOpenState

        console.log({nextOpenState, e})
        onInteraction && onInteraction(this.open, e)
      }
    }
    else {
      onInteraction && onInteraction(nextOpenState, e)
    }
  }
}

const MyPopover2StoreContext = React.createContext<MyPopover2Store>(null)
export const useMyPopover2Store = () => React.useContext(MyPopover2StoreContext)

export const MyPopover2 = observer<MyPopover2Props & { wrapperClassName?: string}>(
  function MyPopover2({
    cache, lazy, onOpened, onClosed, interactionKind, children, portalClassName, popoverClassName, wrapperClassName, isOpen, ...props
  }) {

    const obsProps = useAsObservableSource(({onOpened, onClosed, onInteraction: props.onInteraction, interactionKind, isOpen})),
      store = useLocalStore(() => new MyPopover2Store(obsProps)),
      {setRef, onClickWrapper, handleMouseEnter, handleMouseLeave, onInteraction, onOpening} = store

    useEffect(() => store.registerEffects())

    const extraProps = useMemo(() => {
      return !cache ? {isOpen, lazy} : {isOpen: true, lazy: false}
    }, [cache, isOpen, lazy])

    return (
      <MyPopover2StoreContext.Provider value={store}>
        <div onClick={onClickWrapper} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={classNames(css.popoverWrapper, wrapperClassName)}>
          <Popover2
            {...props}
            interactionKind={interactionKind}
            ref={setRef}
            positioningStrategy='fixed'
            autoFocus={false}
            enforceFocus={false}
            usePortal
            isReallyOpen={store.open}
            portalClassName={classNames(css.portal, portalClassName)}
            popoverClassName={classNames(css.popover, store.open && css.open, popoverClassName)}
            onInteraction={onInteraction}
            onOpening={onOpening}
            {...extraProps}
            modifiers={{
              eventListeners: {
                options: {
                  scroll: false,
                  // resize: false
                }
              },
              ...(props.modifiers ?? {})
            }}
          >
            {children}
          </Popover2>
        </div>
      </MyPopover2StoreContext.Provider>
    )
  })

MyPopover2.defaultProps = {cache: true}
// endregion


// WEBPACK FOOTER //
// ./src/shared/components/ui/widgets/MyPopover2.tsx